import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import type { Tables, TablesInsert, TablesUpdate } from '@/types/supabase'

type IdolMember = Tables<'idol_members'>

export function useMembers() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['idol-members', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('idol_members')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('display_order', { ascending: true })

      if (error) throw error
      return data as IdolMember[]
    },
  })
}

export function useCreateMember() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: Omit<TablesInsert<'idol_members'>, 'tenant_id'>) => {
      const { data, error } = await supabase
        .from('idol_members')
        .insert({ ...input, tenant_id: tenantId! })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['idol-members', tenantId] })
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
    },
  })
}

export function useUpdateMember() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ id, ...update }: TablesUpdate<'idol_members'> & { id: string }) => {
      const { data, error } = await supabase
        .from('idol_members')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['idol-members', tenantId] })
      if (data?.id) qc.invalidateQueries({ queryKey: ['idol-member', data.id] })
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
    },
  })
}

export function useMember(id: string | undefined) {
  return useQuery({
    queryKey: ['idol-member', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('idol_members')
        .select('*')
        .eq('id', id!)
        .single()
      if (error) throw error
      return data as IdolMember
    },
  })
}

export function useDeleteMember() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('idol_members').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['idol-members', tenantId] })
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
    },
  })
}
